import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Card,
  Grid,
  CircularProgress,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import AppsIcon from '@mui/icons-material/Apps';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import LogoutIcon from '@mui/icons-material/Logout';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { HM_OWN_API_BASE, HM_OWN_API_KEY } from './AppConnectPage';
import { getApiUrl } from '../../utils/apiUrl';

const SELECTED_APP_ID_KEY = 'selectedAppId';
const SELECTED_APP_STAFF_KEY = 'selectedAppStaff';
const SELECTED_APP_NAME_KEY = 'selectedAppName';
const SELECTED_APP_USER_HAS_ACCESS_KEY = 'selectedAppUserHasAccess';
const STAFF_DEPARTMENT_IDS_KEY = 'staff_department_ids';
const APPS_URL = getApiUrl('/api/clients/apps/');

const SelectAppPage = () => {
  const navigate = useNavigate();
  const { currentUser, setUserFromApi, logout } = useApp();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [openConnectDialog, setOpenConnectDialog] = useState(false);
  const [connectAccepted, setConnectAccepted] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [showAnnouncementAfterConnect, setShowAnnouncementAfterConnect] = useState(false);
  const [announcementZoomOpen, setAnnouncementZoomOpen] = useState(false);

  // รีเซ็ต localStorage เหมือนปุ่ม "ย้อนกลับไปเลือกแอป" ทุกครั้งที่เปิดหน้านี้
  useEffect(() => {
    localStorage.removeItem(SELECTED_APP_ID_KEY);
    localStorage.removeItem(SELECTED_APP_STAFF_KEY);
    localStorage.removeItem(SELECTED_APP_NAME_KEY);
    localStorage.removeItem(SELECTED_APP_USER_HAS_ACCESS_KEY);
    localStorage.removeItem(STAFF_DEPARTMENT_IDS_KEY);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const verifyUrl = getApiUrl('/api/user/auth/verify/');
    axios
      .get(verifyUrl, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setUserFromApi({ user: data }))
      .catch(() => {});
    // เรียก verify แค่ครั้งเดียวเมื่อเปิดหน้า (setUserFromApi เปลี่ยน reference ทุก render จึงไม่ใส่ใน deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!APPS_URL) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(APPS_URL)
      .then(({ data }) => {
        const raw = data?.results ?? (Array.isArray(data) ? data : []);
        const list = raw.filter((app) => app != null);
        setApps(list);
      })
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectApp = (app) => {
    if (app?.id != null) {
      setSelectedApp(app);
      setConnectError(null);
      setConnectAccepted(false);
      setOpenConnectDialog(true);
    }
  };

  const getUsername = () => currentUser?.username ?? '';

  /** True when user's groups match this app (e.g. user group "ARMIS - ประปา" matches app.groups, app.staff, or app name) */
  const userCanManageApp = (app) => {
    const userGroups = currentUser?.groups ?? [];
    if (userGroups.length === 0) return false;
    const match = (u, a) => {
      const uu = String(u);
      const aa = String(a);
      return uu === aa || aa.includes(uu) || uu.includes(aa);
    };
    const appGroups = Array.isArray(app?.groups) ? app.groups : [];
    const appStaff = Array.isArray(app?.staff) ? app.staff : [];
    const appStrings = [
      ...appGroups,
      ...appStaff.map((s) => (typeof s === 'string' ? s : s?.name ?? s?.group ?? '')),
    ].filter(Boolean);
    if (appStrings.length > 0) {
      return userGroups.some((ug) => appStrings.some((as) => match(ug, as)));
    }
    const appName = String(app?.name ?? '').toLowerCase();
    return userGroups.some((ug) => {
      const prefix = String(ug).split(/\s*-\s*/)[0]?.trim().toLowerCase();
      return prefix && appName.includes(prefix);
    });
  };

  const handleConnect = async () => {
    if (!connectAccepted) {
      setConnectError('กรุณายอมรับการแชร์ข้อมูลก่อนดำเนินการ');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setConnectError('เกิดข้อผิดพลาด กรุณาเข้าสู่ระบบใหม่');
      setTimeout(() => {
        localStorage.clear();
        navigate('/login', { replace: true });
      }, 1500);
      return;
    }
    let username = getUsername();
    const verifyUrl = getApiUrl('/api/user/auth/verify/');
    try {
      const { data } = await axios.get(verifyUrl, { headers: { Authorization: `Token ${token}` } });
      if (Array.isArray(data?.staff_department_ids)) {
        localStorage.setItem(STAFF_DEPARTMENT_IDS_KEY, JSON.stringify(data.staff_department_ids));
      }
      if (!username) username = data?.username ?? '';
    } catch (_) {}
    if (!username) {
      setConnectError('ไม่พบชื่อผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }
    setConnectError(null);
    setConnectLoading(true);
    const url = `${HM_OWN_API_BASE}/${username}/hm_own_api`;
    try {
      const { data } = await axios.post(url, { key: HM_OWN_API_KEY });
      if (data?.status == null) {
        setConnectError('Authentication fail');
        return;
      }
      const homeCode = data?.home?.home_code ?? '';
      const mobilePhone = data?.owner?.MobilePhone ?? '';
      const addressStr = homeCode && mobilePhone ? `${homeCode} | ${mobilePhone}` : homeCode || '';
      const addressUrl = getApiUrl('/api/user/me/address/');
      await axios.put(addressUrl, { address: addressStr }, { headers: { Authorization: `Token ${token}` } });
      if (selectedApp) {
        localStorage.setItem(SELECTED_APP_ID_KEY, String(selectedApp.id));
        localStorage.setItem(SELECTED_APP_STAFF_KEY, JSON.stringify(selectedApp.staff ?? []));
        localStorage.setItem(SELECTED_APP_NAME_KEY, String(selectedApp.name || ''));
        localStorage.setItem(SELECTED_APP_USER_HAS_ACCESS_KEY, String(selectedApp.user_has_access === true));
      }
      setOpenConnectDialog(false);
      setShowAnnouncementAfterConnect(true);
    } catch (err) {
      setConnectError(err?.response?.data?.message || err?.message || 'Authentication fail');
    } finally {
      setConnectLoading(false);
    }
  };

  if (showAnnouncementAfterConnect) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F5F0EB',
          backgroundImage: 'linear-gradient(to left, rgba(48, 176, 235, 0.06), rgba(132, 94, 194, 0.06))',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.7)', mb: 2, textAlign: 'center' }}>
          ประกาศบ้านพักอาศัย
        </Typography>
        <Card
          elevation={0}
          sx={{
            maxWidth: 420,
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/announcement-pr.png`}
            alt="ประกาศการแจ้งปัญหาและข้อขัดข้องผ่านระบบแอปพลิเคชัน"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              verticalAlign: 'middle',
            }}
          />
          <IconButton
            onClick={(e) => { e.stopPropagation(); setAnnouncementZoomOpen(true); }}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
            }}
            aria-label="ซูมดูรูป"
          >
            <ZoomInIcon sx={{ color: 'rgba(0,0,0,0.7)', fontSize: 22 }} />
          </IconButton>
        </Card>
        <Dialog
          open={announcementZoomOpen}
          onClose={() => setAnnouncementZoomOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              maxWidth: '95vw',
              maxHeight: '95vh',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.98)',
            },
          }}
        >
          <DialogContent sx={{ p: 0, '&:first-of-type': { py: 0 } }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL || ''}/announcement-pr.png`}
              alt="ประกาศ (ซูม)"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </DialogContent>
        </Dialog>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{
            mt: 2.5,
            borderRadius: 2,
            px: 3,
            py: 1.25,
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: '#845EC2',
            '&:hover': { bgcolor: '#6344A4' },
          }}
        >
          ปิด
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F9FBFF',
        backgroundImage: 'linear-gradient(to left, rgba(48, 176, 235, 0.09), rgba(132, 94, 194, 0.09))',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(to left, rgb(48, 176, 235), rgba(132, 94, 194, 0.8))',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ py: 2, minHeight: '80px !important', position: 'relative' }}>
          <Box sx={{
            position: { xs: 'static', md: 'absolute' },
            left: { xs: 0, md: '50%' },
            transform: { xs: 'none', md: 'translateX(-50%)' },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: { xs: 'flex-start', md: 'center' },
          }}>
            <SupportAgentIcon sx={{ fontSize: 40, color: 'rgba(0, 0, 0, 0.6)' }} />
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', md: 'center' },
              textAlign: { xs: 'left', md: 'center' },
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, letterSpacing: 0.5, color: 'rgba(0, 0, 0, 0.6)', textAlign: 'inherit' }}>
                Assist
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}> System</Box>
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={{
                  fontSize: '0.8rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'inherit',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                ระบบรับแจ้งปัญหา - ข้อขัดข้องของกองทัพอากาศ
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, ml: 'auto' }}>
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              variant="contained"
              sx={{
                borderRadius: 1,
                bgcolor: '#845EC2',
                color: 'white',
                fontWeight: 500,
                px: 2,
                '&:hover': { bgcolor: '#6344A4' },
              }}
            >
              ออกจากระบบ
            </Button>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto', gap: 1 }}>
            <IconButton
              onClick={handleLogout}
              sx={{
                bgcolor: '#845EC2',
                color: 'white',
                '&:hover': { bgcolor: '#6344A4' },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          background: `
            linear-gradient(160deg, #faf8fc 0%, #f5f0fa 25%, #eef6fc 50%, #f8f4f0 75%, #f0eef8 100%)
          `,
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 4, sm: 7 },
          px: 2,
          '&::before': {
          content: '""',
          position: 'absolute',
          top: '-40%',
          right: '-30%',
          width: '90%',
          height: '90%',
          borderRadius: '50%',
          background: `radial-gradient(circle at 70% 30%, ${alpha('#845EC2', 0.08)} 0%, transparent 55%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          left: '-25%',
          width: '70%',
          height: '70%',
          borderRadius: '50%',
          background: `radial-gradient(circle at 30% 70%, ${alpha('#30B0EB', 0.06)} 0%, transparent 55%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 960 }}>
        {/* Header: ไอคอนบรรทัดเดียวกับข้อความ */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: { xs: 4, sm: 5 },
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              background: `linear-gradient(145deg, ${alpha('#845EC2', 0.15)} 0%, ${alpha('#6344A4', 0.08)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid',
              borderColor: alpha('#845EC2', 0.22),
              boxShadow: `0 4px 20px ${alpha('#845EC2', 0.1)}`,
            }}
          >
            <AppsIcon sx={{ fontSize: 32, color: '#6344A4' }} />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
                letterSpacing: '-0.01rem',
                mb: 0.25,
                textShadow: '0 1px 2px rgba(0,0,0,0.04)',
                fontSize: '1rem',
                lineHeight: 1.3,
              }}
            >
              เลือกแอปพลิเคชัน
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5c5c5c',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              เลือกแอปที่ต้องการใช้งาน
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
            <CircularProgress size={44} thickness={3.6} sx={{ color: '#845EC2' }} />
            <Typography variant="body2" sx={{ color: '#7a7a7a' }}>กำลังโหลด...</Typography>
          </Box>
        ) : apps.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              bgcolor: alpha('#fff', 0.9),
              borderRadius: 3,
              px: 4,
              py: 5,
              textAlign: 'center',
              border: '1px solid',
              borderColor: alpha('#845EC2', 0.1),
              boxShadow: `0 2px 16px ${alpha('#845EC2', 0.06)}`,
            }}
          >
            <AppsIcon sx={{ fontSize: 48, color: alpha('#845EC2', 0.35), mb: 1.5 }} />
            <Typography sx={{ color: '#6a6a6a', fontSize: '0.9375rem' }}>
              ไม่พบแอปพลิเคชัน
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2.5} justifyContent="center">
            {apps.map((app) => (
              <Grid
                item
                xs={12}
                sm={apps.length === 1 ? 12 : 4}
                md={apps.length === 1 ? 12 : 3}
                key={app.id}
              >
                <Box sx={apps.length === 1 ? { maxWidth: 260, mx: 'auto' } : {}}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: alpha('#845EC2', 0.12),
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: `0 2px 12px ${alpha('#845EC2', 0.06)}`,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: '100%',
                    '&:hover': {
                      borderColor: alpha('#845EC2', 0.3),
                      boxShadow: `0 12px 32px ${alpha('#845EC2', 0.14)}`,
                      transform: 'translateY(-2px)',
                      '& .app-name': { color: '#5340a0' },
                      '& .app-icon-wrap': {
                        background: `linear-gradient(145deg, ${alpha('#845EC2', 0.18)} 0%, ${alpha('#6344A4', 0.1)} 100%)`,
                        borderColor: alpha('#845EC2', 0.35),
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: 'center',
                      py: 2,
                      px: 1.5,
                      minHeight: 160,
                      height: '100%',
                    }}
                  >
                    <Box
                      className="app-icon-wrap"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: `linear-gradient(145deg, ${alpha('#845EC2', 0.1)} 0%, ${alpha('#845EC2', 0.05)} 100%)`,
                        border: '1px solid',
                        borderColor: alpha('#845EC2', 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.25,
                        flexShrink: 0,
                        transition: 'all 0.25s ease',
                      }}
                    >
                      <TouchAppIcon sx={{ fontSize: 28, color: '#845EC2' }} />
                    </Box>
                    <Typography
                      className="app-name"
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: '#2d2d2d',
                        lineHeight: 1.3,
                        transition: 'color 0.2s ease',
                        fontSize: '0.9375rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {app.name || 'แอป'}
                    </Typography>
                    {app.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6e6e6e',
                          mt: 0.25,
                          lineHeight: 1.35,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.75rem',
                        }}
                      >
                        {app.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
                      {app.user_has_access === true && (
                        <Chip
                          size="small"
                          label="Admin"
                          sx={{
                            fontWeight: 600,
                            borderRadius: 1,
                            bgcolor: alpha('#845EC2', 0.12),
                            color: '#6344A4',
                            border: '1px solid',
                            borderColor: alpha('#845EC2', 0.3),
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 1, flexWrap: 'nowrap', justifyContent: 'center', mt: 1.25 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AdsClickIcon sx={{ fontSize: 15 }} />}
                        onClick={() => handleSelectApp(app)}
                        sx={{
                          borderRadius: 1,
                          bgcolor: '#845EC2',
                          color: 'white',
                          fontWeight: 600,
                          textTransform: 'none',
                          minHeight: 36,
                          minWidth: 100,
                          py: 0.4,
                          px: 1.25,
                          fontSize: '0.75rem',
                          boxShadow: `0 2px 8px ${alpha('#845EC2', 0.3)}`,
                          '&:hover': { bgcolor: '#6344A4', boxShadow: `0 4px 12px ${alpha('#845EC2', 0.35)}` },
                        }}
                      >
                        แจ้งปัญหา
                      </Button>
                      {userCanManageApp(app) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SettingsIcon sx={{ fontSize: 15 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              localStorage.setItem(SELECTED_APP_ID_KEY, String(app.id));
                              localStorage.setItem(SELECTED_APP_STAFF_KEY, JSON.stringify(app.staff ?? []));
                              localStorage.setItem(SELECTED_APP_NAME_KEY, String(app.name || ''));
                              localStorage.setItem(SELECTED_APP_USER_HAS_ACCESS_KEY, 'true');
                              navigate('/');
                            }}
                            sx={{
                              borderRadius: 1,
                              color: '#845EC2',
                              borderColor: alpha('#845EC2', 0.5),
                              fontWeight: 600,
                              textTransform: 'none',
                              minHeight: 36,
                              minWidth: 100,
                              py: 0.4,
                              px: 1.25,
                              fontSize: '0.75rem',
                              '&:hover': { borderColor: '#845EC2', bgcolor: alpha('#845EC2', 0.08) },
                            }}
                          >
                            จัดการ
                          </Button>
                         
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Popup เชื่อมต่อระบบ */}
      <Dialog
        open={openConnectDialog}
        onClose={() => !connectLoading && setOpenConnectDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha('#845EC2', 0.12),
            boxShadow: `0 24px 48px ${alpha('#845EC2', 0.12)}`,
          },
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha('#845EC2', 0.08)} 0%, ${alpha('#6344A4', 0.05)} 100%)`,
            borderBottom: '1px solid',
            borderColor: alpha('#845EC2', 0.1),
            px: 3,
            pt: 3,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 ,  }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: alpha('#845EC2', 0.12),
                border: '1px solid',
                borderColor: alpha('#845EC2', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LinkIcon sx={{ color: '#6344A4', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                เชื่อมต่อระบบ
              </Typography>
              <Typography variant="body2" sx={{ color: '#5c5c5c', mt: 0.25 }}>
                แชร์ข้อมูลระหว่างแอปพลิเคชัน
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#5c5c5c', mb: 2, lineHeight: 1.6 }}>
            เพื่อเชื่อมต่อและแชร์ข้อมูลระหว่างระบบ กรุณายอมรับเงื่อนไขด้านล่างก่อนดำเนินการ
          </Typography>
          {connectError && (
            <Alert
              severity="error"
              onClose={() => setConnectError(null)}
              sx={{
                mb: 2,
                borderRadius: 1.5,
                '& .MuiAlert-message': { fontSize: '0.875rem' },
              }}
            >
              {connectError}
            </Alert>
          )}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#845EC2', 0.15),
              bgcolor: alpha('#845EC2', 0.03),
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={connectAccepted}
                  onChange={(e) => setConnectAccepted(e.target.checked)}
                  sx={{ color: '#845EC2', '&.Mui-checked': { color: '#845EC2' } }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0,0,0,0.75)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                  }}
                >
                  ยอมรับการแชร์ข้อมูลระหว่างระบบ
                </Typography>
              }
              sx={{ m: 0, display: 'flex', alignItems: 'center' }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: alpha('#845EC2', 0.08),
            bgcolor: alpha('#f8f6fc', 0.8),
          }}
        >
          <Button
            onClick={() => setOpenConnectDialog(false)}
            disabled={connectLoading}
            sx={{
              borderRadius: 1.5,
              px: 2.5,
              textTransform: 'none',
              color: '#5c5c5c',
            }}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connectLoading}
            sx={{
              borderRadius: 1.5,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#845EC2',
              boxShadow: `0 2px 8px ${alpha('#845EC2', 0.3)}`,
              '&:hover': {
                bgcolor: '#6344A4',
                boxShadow: `0 4px 12px ${alpha('#845EC2', 0.35)}`,
              },
            }}
          >
            {connectLoading ? (
              <CircularProgress size={22} sx={{ color: 'white' }} />
            ) : (
              'ยืนยัน'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default SelectAppPage;
export { SELECTED_APP_ID_KEY, SELECTED_APP_STAFF_KEY, SELECTED_APP_NAME_KEY, SELECTED_APP_USER_HAS_ACCESS_KEY, STAFF_DEPARTMENT_IDS_KEY };
